import { useEffect } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { useSelector } from 'react-redux';
import useShowingExitConfirmation from './useShowingExitConfirmation';
import { selectFeatureFlags } from '../state/slices/slice-link-params';
export var EXIT_OVERLAY_CONTAINER_ID = 'pinwheel-exit-overlay';
export var MODAL_CONTENT_CONTAINER_ID = 'pinwheel-link-modal';
// With mobile screen readers (iOS VoiceOver, Android Talkback), there is no
// standardized way in which the initial focus/selection is set whenever a new
// screen is loaded. Thus, we want to programmatically set the focus to the
// header/title element so that it will be read immediately. Additionally, this
// ensures that navigation will (usually) start near the top of screen, so that
// the user can sequentially navigate through the content from top to bottom.
// https://www.notion.so/pinwheelapi/WCAG-Mobile-Screen-Reader-Support-c014127072dc471c9c20898a95f7f181
export var useScreenHeaderFocus = function (_a) {
    var _b = _a === void 0 ? {} : _a, elementId = _b.elementId, _c = _b.enabled, enabled = _c === void 0 ? true : _c;
    var showingExitConfirmation = useShowingExitConfirmation().showingExitConfirmation;
    // HACK: `useSelector` instead of `useAppSelector` to avoid a circular dependency
    var featureFlags = useSelector(selectFeatureFlags);
    useEffect(function () {
        if (!enabled || !featureFlags.focusScreenHeader)
            return;
        if (elementId) {
            document.getElementById(elementId).focus();
            return;
        }
        // When the exit confirmation is shown, the previous screen's content is
        // visually hidden but not removed from the DOM. So we need to query off of
        // the correct container element in order to avoid unintentionally selecting
        // a hidden element.
        var containerId = showingExitConfirmation
            ? EXIT_OVERLAY_CONTAINER_ID
            : MODAL_CONTENT_CONTAINER_ID;
        var containerElement = document.getElementById(containerId);
        if (!containerElement) {
            // This should never happen, but we should log anyways in case there are
            // broken assumptions or bugs.
            datadogLogs.logger.error('useScreenHeaderFocus: Unable to find container element on current screen', { containerId: containerId });
            return;
        }
        // Text-based input components on the login screens will have autofocus set,
        // so don't apply focus in that case. We need to check for `data-autofocus`
        // since React doesn't explicitly set the `autofocus` attribute when used.
        if (containerElement.querySelector('[data-autofocus="true"]')) {
            return;
        }
        var headerElement = containerElement
            // Find the first (i.e. topmost) match on the screen, as this will usually
            // be screen's title/header. In the future, we may want to make this more
            // robust by explicitly setting/enforcing an accessibility header ID on
            // every screen/view that we query for instead.
            .querySelector('h1, h2, h3, h4, h5, h6');
        if (headerElement) {
            // Make element programmatically focusable, but not manually tabbable.
            headerElement.setAttribute('tabindex', '-1');
            // Prevents showing a visual outline on hover when the user is not using
            // a screen reader. Screen readers will already highlight/outline the
            // current focused text on a page so this default outline is unnecessary.
            headerElement.style.outline = 'none';
            headerElement.focus();
        }
        else {
            // If this happens, we have broken assumptions/bugs (e.g. we're assuming
            // that a header element has already completed mounting when it hasn't
            // yet, or the screen title element isn't actually a header tag, etc).
            datadogLogs.logger.error('useScreenHeaderFocus: Unable to find header element on current screen', { containerId: containerId });
        }
    }, [
        elementId,
        enabled,
        featureFlags.focusScreenHeader,
        showingExitConfirmation,
    ]);
};
